import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { ScopeProvider } from '@/lib/scope';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/pages/Login';
import { HomePage } from '@/pages/Home';
import { WarehousesPage } from '@/pages/admin/Warehouses';
import { ConfigPage } from '@/pages/admin/Config';
import { SeasonsConfigPage } from '@/pages/admin/SeasonsConfig';
import { NormsConfigPage } from '@/pages/admin/NormsConfig';
import { CustomerNormsConfigPage } from '@/pages/admin/CustomerNormsConfig';
import { BulkNormsConfigPage } from '@/pages/admin/BulkNormsConfig';
import { HistoryImportPage } from '@/pages/admin/HistoryImport';
import { SubmitAdjustmentPage } from '@/pages/planner/SubmitAdjustment';
import { ForecastOverviewPage } from '@/pages/planner/ForecastOverview';
import { ForecastReviewPage } from '@/pages/planner/ForecastReview';
import { ForecastWarehousePage } from '@/pages/planner/ForecastWarehouse';
import { CustomerListPage } from '@/pages/planner/CustomerList';
import { CustomerDetailPage } from '@/pages/planner/CustomerDetail';
import { PlaceholderPage } from '@/pages/Placeholder';

function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Loader2
          style={{
            width: 24,
            height: 24,
            color: 'var(--bx-text-muted)',
            animation: 'wfpSpin 1s linear infinite',
          }}
        />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <ScopeProvider>
      <AppLayout />
    </ScopeProvider>
  );
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<HomePage />} />

        {/* Planner */}
        <Route path="/planner/forecast" element={<ForecastOverviewPage />} />
        <Route path="/planner/forecast/review" element={<ForecastReviewPage />} />
        <Route path="/planner/forecast/customer" element={<CustomerListPage />} />
        <Route path="/planner/forecast/customer/:id" element={<CustomerDetailPage />} />
        <Route path="/planner/forecast/warehouse" element={<ForecastWarehousePage />} />
        <Route
          path="/planner/shift-plan"
          element={
            <PlaceholderPage
              title="Kế hoạch ca"
              phase="post-MVP"
              breadcrumb={['Lập kế hoạch', 'Kế hoạch ca']}
              description="5-layer workforce planning — defer sang WFP v2 sau MVP."
            />
          }
        />
        <Route
          path="/planner/hiring"
          element={
            <PlaceholderPage
              title="Tuyển dụng"
              phase="post-MVP"
              breadcrumb={['Lập kế hoạch', 'Tuyển dụng']}
            />
          }
        />
        <Route path="/planner/override-import" element={<SubmitAdjustmentPage />} />

        {/* Admin */}
        <Route path="/admin/warehouses" element={<WarehousesPage />} />
        <Route
          path="/admin/staff"
          element={
            <PlaceholderPage
              title="Nhân sự"
              phase="post-MVP"
              breadcrumb={['Quản trị', 'Nhân sự']}
            />
          }
        />
        <Route path="/admin/history-import" element={<HistoryImportPage />} />
        <Route path="/admin/config" element={<ConfigPage />} />
        <Route path="/admin/config/dinhmuc" element={<BulkNormsConfigPage />} />
        <Route path="/admin/config/dinhmuc/mac-dinh" element={<NormsConfigPage />} />
        <Route path="/admin/config/dinhmuc-khachhang" element={<CustomerNormsConfigPage />} />
        <Route path="/admin/config/dinhmuc-bang" element={<Navigate to="/admin/config/dinhmuc" replace />} />
        <Route path="/admin/config/thamso" element={<SeasonsConfigPage />} />

        {/* Audit (disabled in sidebar but route exists) */}
        <Route
          path="/audit/activity"
          element={
            <PlaceholderPage
              title="Nhật ký hoạt động"
              phase="post-MVP"
              breadcrumb={['Kiểm toán', 'Nhật ký hoạt động']}
            />
          }
        />
        <Route
          path="/audit/plans"
          element={
            <PlaceholderPage
              title="Lưu trữ kế hoạch"
              phase="post-MVP"
              breadcrumb={['Kiểm toán', 'Lưu trữ kế hoạch']}
            />
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
